import React, { useEffect, useState } from "react";
import { fetchProducts, createProduct, updateProduct, deleteProduct, fetchOrders, fetchOffers, acceptOffer, rejectOffer, counterOffer, deleteOffer } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { NeweggImportDialog } from "../components/NeweggImportDialog";
import { TrackerTab } from "../components/TrackerTab";
import { StockWatchTab } from "../components/StockWatchTab";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Package, Receipt, Handshake, Check, X, ArrowsLeftRight, DownloadSimple, Truck, Eye } from "@phosphor-icons/react";

const EMPTY_FORM = {
  name: "",
  brand: "",
  category: "",
  price: 0,
  original_price: 0,
  image: "",
  description: "",
  stock: 10,
  rating: 4.5,
  reviews_count: 0,
  sku: "",
  is_deal: false,
  newegg_url: "",
};

export default function Admin() {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [counterDialog, setCounterDialog] = useState({ open: false, offer: null, price: "", note: "" });
  const [importOpen, setImportOpen] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [p, o, of] = await Promise.all([fetchProducts({}), fetchOrders(), fetchOffers()]);
      setProducts(p);
      setOrders(o);
      setOffers(of);
    } catch (e) {
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      brand: p.brand,
      category: p.category,
      price: p.price,
      original_price: p.original_price || 0,
      image: p.image,
      description: p.description,
      stock: p.stock,
      rating: p.rating,
      reviews_count: p.reviews_count,
      sku: p.sku || "",
      is_deal: p.is_deal || false,
      newegg_url: p.newegg_url || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        original_price: parseFloat(form.original_price) || null,
        stock: parseInt(form.stock) || 0,
        rating: parseFloat(form.rating) || 0,
        reviews_count: parseInt(form.reviews_count) || 0,
        specs: editing?.specs || {},
        images: editing?.images || [],
      };
      if (!payload.original_price) delete payload.original_price;
      if (editing) {
        await updateProduct(editing.id, payload);
        toast.success("Product updated");
      } else {
        await createProduct(payload);
        toast.success("Product created");
      }
      setOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await deleteProduct(id);
      toast.success("Deleted");
      loadAll();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-12" data-testid="admin-page">
      <div className="mb-8">
        <div className="font-mono uppercase text-xs tracking-[0.2em] text-[#002FA7] mb-2">ADMIN</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">Control Room</h1>
        <p className="text-zinc-700 mt-2">Manage your catalog and orders.</p>
      </div>

      <Tabs defaultValue="products">
        <TabsList className="rounded-none bg-zinc-100 p-1 mb-6 flex flex-nowrap sm:flex-wrap justify-start overflow-x-auto h-auto gap-1 w-full sm:w-auto">
          <TabsTrigger value="products" className="rounded-none font-mono uppercase tracking-wider text-[10px] sm:text-xs h-10 sm:h-9 shrink-0" data-testid="tab-products">
            <Package size={14} weight="bold" className="mr-1 sm:mr-2 shrink-0" /> <span className="truncate">Products ({products.length})</span>
          </TabsTrigger>
          <TabsTrigger value="orders" className="rounded-none font-mono uppercase tracking-wider text-[10px] sm:text-xs h-10 sm:h-9 shrink-0" data-testid="tab-orders">
            <Receipt size={14} weight="bold" className="mr-1 sm:mr-2 shrink-0" /> <span className="truncate">Orders ({orders.length})</span>
          </TabsTrigger>
          <TabsTrigger value="offers" className="rounded-none font-mono uppercase tracking-wider text-[10px] sm:text-xs h-10 sm:h-9 shrink-0" data-testid="tab-offers">
            <Handshake size={14} weight="bold" className="mr-1 sm:mr-2 shrink-0" /> <span className="truncate">Offers ({offers.filter(o => o.status === "pending").length}/{offers.length})</span>
          </TabsTrigger>
          <TabsTrigger value="tracker" className="rounded-none font-mono uppercase tracking-wider text-[10px] sm:text-xs h-10 sm:h-9 shrink-0" data-testid="tab-tracker">
            <Truck size={14} weight="bold" className="mr-1 sm:mr-2 shrink-0" /> <span className="truncate">Tracker</span>
          </TabsTrigger>
          <TabsTrigger value="stock-watch" className="rounded-none font-mono uppercase tracking-wider text-[10px] sm:text-xs h-10 sm:h-9 shrink-0" data-testid="tab-stock-watch">
            <Eye size={14} weight="bold" className="mr-1 sm:mr-2 shrink-0" /> <span className="truncate">Stock Watch</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="products">
          <div className="mb-4 flex justify-between items-center flex-wrap gap-2">
            <p className="font-mono text-sm text-zinc-500 uppercase tracking-wider">
              {loading ? "Loading..." : `${products.length} items`}
            </p>
            <div className="flex gap-2">
              <Button
                onClick={() => setImportOpen(true)}
                variant="outline"
                data-testid="open-newegg-import-button"
                className="rounded-none border-2 border-[#FF2A00] text-[#FF2A00] hover:bg-[#FF2A00] hover:text-white font-bold uppercase tracking-wider"
              >
                <DownloadSimple size={16} weight="bold" className="mr-2" /> Import from Newegg
              </Button>
              <Button onClick={openNew} className="rounded-none bg-zinc-950 hover:bg-[#002FA7] text-white font-bold uppercase tracking-wider" data-testid="add-product-button">
                <Plus size={16} weight="bold" className="mr-2" /> New product
              </Button>
            </div>
          </div>

          <div className="border border-zinc-200">
            <table className="w-full text-sm" data-testid="products-table">
              <thead className="bg-zinc-950 text-white">
                <tr>
                  <th className="text-left p-3 font-mono uppercase text-xs tracking-wider">Product</th>
                  <th className="text-left p-3 font-mono uppercase text-xs tracking-wider hidden md:table-cell">Category</th>
                  <th className="text-left p-3 font-mono uppercase text-xs tracking-wider">Price</th>
                  <th className="text-left p-3 font-mono uppercase text-xs tracking-wider hidden md:table-cell">Stock</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-t border-zinc-100" data-testid={`admin-product-row-${p.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <img src={p.image} alt={p.name} className="w-12 h-12 object-cover border border-zinc-200" />
                        <div>
                          <div className="font-semibold">{p.name}</div>
                          <div className="font-mono text-xs text-zinc-500">{p.brand}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 font-mono text-xs hidden md:table-cell">{p.category}</td>
                    <td className="p-3 font-mono font-bold">${p.price.toFixed(2)}</td>
                    <td className="p-3 font-mono hidden md:table-cell">{p.stock}</td>
                    <td className="p-3">
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="rounded-none border-zinc-300" data-testid={`edit-product-${p.id}`}>
                          <PencilSimple size={14} weight="bold" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => remove(p.id)} className="rounded-none border-red-300 text-red-600 hover:bg-red-50" data-testid={`delete-product-${p.id}`}>
                          <Trash size={14} weight="bold" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="orders">
          {orders.length === 0 ? (
            <div className="text-center py-16 border border-zinc-200">
              <Receipt size={48} weight="duotone" className="mx-auto mb-3 text-zinc-300" />
              <p className="font-mono uppercase text-xs tracking-[0.2em] text-zinc-500">No orders yet</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="orders-list">
              {orders.map((o) => (
                <div key={o.id} className="border border-zinc-200 p-4" data-testid={`order-${o.id}`}>
                  <div className="flex flex-wrap justify-between items-start gap-2 mb-3">
                    <div>
                      <div className="font-mono text-xs uppercase tracking-wider text-zinc-500">
                        Order · {o.id.slice(0, 8)}
                      </div>
                      <div className="font-semibold">{o.customer_email || "Guest"}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-2xl font-bold">${o.amount.toFixed(2)}</div>
                      <div className="font-mono text-xs uppercase tracking-wider text-green-600">{o.status}</div>
                    </div>
                  </div>
                  <ul className="text-sm space-y-1 pt-3 border-t border-zinc-100">
                    {(o.items || []).map((it, idx) => (
                      <li key={idx} className="flex justify-between font-mono">
                        <span>{it.name} × {it.quantity}</span>
                        <span>${it.line_total.toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="offers">
          {offers.length === 0 ? (
            <div className="text-center py-16 border border-zinc-200" data-testid="offers-empty">
              <Handshake size={48} weight="duotone" className="mx-auto mb-3 text-zinc-300" />
              <p className="font-mono uppercase text-xs tracking-[0.2em] text-zinc-500">No offers yet</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="offers-list">
              {offers.map((o) => {
                const statusColor = {
                  pending: "bg-amber-100 text-amber-900 border-amber-300",
                  accepted: "bg-green-100 text-green-900 border-green-300",
                  rejected: "bg-red-100 text-red-900 border-red-300",
                  countered: "bg-blue-100 text-blue-900 border-blue-300",
                  paid_pending: "bg-violet-100 text-violet-900 border-violet-300",
                }[o.status] || "bg-zinc-100";
                const discountPct = Math.round(
                  ((o.original_price - o.offered_price) / o.original_price) * 100,
                );
                return (
                  <div
                    key={o.id}
                    className="border border-zinc-200 p-4"
                    data-testid={`offer-${o.id}`}
                  >
                    <div className="flex gap-4 flex-wrap">
                      <img
                        src={o.product_image}
                        alt={o.product_name}
                        className="w-20 h-20 object-cover border border-zinc-200"
                      />
                      <div className="flex-1 min-w-[200px]">
                        <div className="font-mono text-xs uppercase tracking-wider text-zinc-500">
                          Offer · {o.id.slice(0, 8)} ·{" "}
                          {new Date(o.created_at).toLocaleDateString()}
                        </div>
                        <h4 className="font-heading font-bold text-base mt-1">
                          {o.product_name}
                        </h4>
                        <div className="font-mono text-xs text-zinc-600 mt-1">
                          {o.buyer_name} · {o.buyer_email}
                        </div>
                        {o.message && (
                          <p className="text-xs text-zinc-700 mt-2 italic font-mono">
                            "{o.message}"
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="font-mono uppercase text-[10px] tracking-[0.2em] text-zinc-500">
                          Listed
                        </div>
                        <div className="font-mono text-sm text-zinc-400 line-through">
                          ${o.original_price.toFixed(2)}
                        </div>
                        <div className="font-mono uppercase text-[10px] tracking-[0.2em] text-zinc-500 mt-1">
                          Offered
                        </div>
                        <div className="font-mono text-2xl font-bold">
                          ${o.offered_price.toFixed(2)}
                        </div>
                        <div className="font-mono text-xs uppercase tracking-wider text-[#FF2A00]">
                          -{discountPct}%
                        </div>
                        {o.counter_price && (
                          <>
                            <div className="font-mono uppercase text-[10px] tracking-[0.2em] text-zinc-500 mt-2">
                              Counter
                            </div>
                            <div className="font-mono text-lg font-bold text-[#002FA7]">
                              ${o.counter_price.toFixed(2)}
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-zinc-100 flex-wrap gap-2">
                      <Badge
                        className={`rounded-none font-mono uppercase tracking-wider border ${statusColor}`}
                      >
                        {o.status.replace("_", " ")}
                      </Badge>

                      {o.status === "pending" ? (
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            onClick={async () => {
                              try {
                                await acceptOffer(o.id);
                                toast.success("Offer accepted");
                                loadAll();
                              } catch {
                                toast.error("Failed");
                              }
                            }}
                            data-testid={`accept-offer-${o.id}`}
                            className="rounded-none bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wider"
                          >
                            <Check size={14} weight="bold" className="mr-1" /> Accept
                          </Button>
                          <Button
                            size="sm"
                            onClick={() =>
                              setCounterDialog({
                                open: true,
                                offer: o,
                                price: String(o.offered_price + 50),
                                note: "",
                              })
                            }
                            data-testid={`counter-offer-${o.id}`}
                            variant="outline"
                            className="rounded-none border-2 border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white font-bold uppercase tracking-wider"
                          >
                            <ArrowsLeftRight size={14} weight="bold" className="mr-1" /> Counter
                          </Button>
                          <Button
                            size="sm"
                            onClick={async () => {
                              if (!window.confirm("Reject this offer?")) return;
                              try {
                                await rejectOffer(o.id);
                                toast.success("Offer rejected");
                                loadAll();
                              } catch {
                                toast.error("Failed");
                              }
                            }}
                            data-testid={`reject-offer-${o.id}`}
                            variant="outline"
                            className="rounded-none border-2 border-red-500 text-red-600 hover:bg-red-500 hover:text-white font-bold uppercase tracking-wider"
                          >
                            <X size={14} weight="bold" className="mr-1" /> Reject
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            if (!window.confirm("Delete this offer?")) return;
                            try {
                              await deleteOffer(o.id);
                              toast.success("Deleted");
                              loadAll();
                            } catch {
                              toast.error("Failed");
                            }
                          }}
                          data-testid={`delete-offer-${o.id}`}
                          className="rounded-none border-zinc-300 text-zinc-600"
                        >
                          <Trash size={14} weight="bold" />
                        </Button>
                      )}
                    </div>

                    {o.admin_note && (
                      <div className="mt-3 pt-3 border-t border-zinc-100 font-mono text-xs">
                        <span className="uppercase tracking-wider text-zinc-500">Admin note:</span>{" "}
                        {o.admin_note}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="tracker">
          <TrackerTab />
        </TabsContent>

        <TabsContent value="stock-watch">
          <StockWatchTab />
        </TabsContent>
      </Tabs>

      {/* Counter offer dialog */}
      <Dialog
        open={counterDialog.open}
        onOpenChange={(v) => setCounterDialog({ ...counterDialog, open: v })}
      >
        <DialogContent className="rounded-none border-2 border-zinc-950 max-w-md" data-testid="counter-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tight">
              Counter Offer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="font-mono text-sm">
              Buyer offered ${counterDialog.offer?.offered_price.toFixed(2)} on{" "}
              <span className="font-semibold">{counterDialog.offer?.product_name}</span>
            </div>
            <div>
              <label className="font-mono uppercase text-xs tracking-wider">Your counter (USD)</label>
              <Input
                type="number"
                step="0.01"
                value={counterDialog.price}
                onChange={(e) => setCounterDialog({ ...counterDialog, price: e.target.value })}
                className="rounded-none h-12 font-mono text-lg font-bold"
                data-testid="counter-price-input"
              />
            </div>
            <div>
              <label className="font-mono uppercase text-xs tracking-wider">Note to buyer</label>
              <Textarea
                value={counterDialog.note}
                onChange={(e) => setCounterDialog({ ...counterDialog, note: e.target.value })}
                className="rounded-none font-mono text-sm"
                rows={2}
                placeholder="Best I can do given current stock..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCounterDialog({ ...counterDialog, open: false })}
              className="rounded-none"
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                const price = parseFloat(counterDialog.price);
                if (!price || price <= 0) {
                  toast.error("Invalid price");
                  return;
                }
                try {
                  await counterOffer(counterDialog.offer.id, price, counterDialog.note);
                  toast.success("Counter sent");
                  setCounterDialog({ open: false, offer: null, price: "", note: "" });
                  loadAll();
                } catch {
                  toast.error("Failed");
                }
              }}
              data-testid="submit-counter-button"
              className="rounded-none bg-zinc-950 hover:bg-[#002FA7] text-white font-bold uppercase tracking-wider"
            >
              Send Counter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-2 border-zinc-950 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tight">
              {editing ? "Edit Product" : "New Product"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2">
              <label className="font-mono uppercase text-xs tracking-wider">Name</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" data-testid="form-name" />
            </div>
            <div>
              <label className="font-mono uppercase text-xs tracking-wider">Brand</label>
              <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="rounded-none" data-testid="form-brand" />
            </div>
            <div>
              <label className="font-mono uppercase text-xs tracking-wider">Category</label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-none" data-testid="form-category" />
            </div>
            <div>
              <label className="font-mono uppercase text-xs tracking-wider">Price (USD)</label>
              <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-none" data-testid="form-price" />
            </div>
            <div>
              <label className="font-mono uppercase text-xs tracking-wider">Original Price</label>
              <Input type="number" step="0.01" value={form.original_price} onChange={(e) => setForm({ ...form, original_price: e.target.value })} className="rounded-none" />
            </div>
            <div>
              <label className="font-mono uppercase text-xs tracking-wider">Stock</label>
              <Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="rounded-none" />
            </div>
            <div>
              <label className="font-mono uppercase text-xs tracking-wider">SKU</label>
              <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="rounded-none" />
            </div>
            <div className="col-span-2">
              <label className="font-mono uppercase text-xs tracking-wider">Image URL</label>
              <Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="rounded-none" data-testid="form-image" />
            </div>
            <div className="col-span-2">
              <label className="font-mono uppercase text-xs tracking-wider">Description</label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none" rows={3} data-testid="form-description" />
            </div>
            <div className="col-span-2">
              <label className="font-mono uppercase text-xs tracking-wider">Newegg URL (optional)</label>
              <Input value={form.newegg_url} onChange={(e) => setForm({ ...form, newegg_url: e.target.value })} className="rounded-none" />
            </div>
            <label className="col-span-2 flex items-center gap-2 font-mono uppercase text-xs tracking-wider mt-2">
              <input type="checkbox" checked={form.is_deal} onChange={(e) => setForm({ ...form, is_deal: e.target.checked })} className="w-4 h-4 accent-[#FF2A00]" />
              Mark as deal
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
            <Button onClick={save} className="rounded-none bg-zinc-950 hover:bg-[#002FA7] text-white font-bold uppercase tracking-wider" data-testid="save-product-button">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <NeweggImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onSuccess={loadAll}
      />
    </div>
  );
}
